/**
 * Client REST adlt/v3 — deux modes d'authentification (§ 2) :
 *   - embed (shortcodes) : cookie WordPress + nonce ;
 *   - standalone (PWA)   : JWT double jeton, rafraîchi automatiquement.
 * File d'attente hors ligne IndexedDB avec clé d'idempotence (§ 11.3).
 */
import { idb } from './idb.js';
import { rejouerFile } from './queue.js';

const CFG = window.ADLT_CONFIG || {};
const REST = (CFG.restUrl || '/wp-json/adlt/v3/').replace(/\/?$/, '/');

const listeners = {};
export const bus = {
  on(evt, fn) { (listeners[evt] = listeners[evt] || []).push(fn); return () => { listeners[evt] = listeners[evt].filter((f) => f !== fn); }; },
  emit(evt, data) { (listeners[evt] || []).forEach((fn) => fn(data)); },
};

/* ---------------- Jetons (mode standalone) ---------------- */

function tokensGet() {
  try { return JSON.parse(localStorage.getItem('adlt_tokens') || 'null'); } catch { return null; }
}
function tokensSet(t) {
  if (t) localStorage.setItem('adlt_tokens', JSON.stringify(t));
  else localStorage.removeItem('adlt_tokens');
  // Miroir IndexedDB : le service worker n'a pas accès au localStorage et doit
  // pouvoir s'authentifier pour rejouer la file sans onglet ouvert (§ 11.3).
  idb.tokensSet(t).catch(() => {});
}

export function isStandalone() { return CFG.mode !== 'embed'; }
export function isLoggedIn() { return isStandalone() ? !!tokensGet() : !!CFG.loggedIn; }

/**
 * Jeton de rafraîchissement de l'appareil courant. Transmis au changement de
 * mot de passe pour que cette session survive pendant que les autres sont
 * révoquées.
 */
export function refreshTokenActuel() {
  const t = tokensGet();
  return t && t.refresh ? t.refresh : '';
}

/**
 * Adopte les jetons du miroir IndexedDB s'ils sont plus récents que ceux du
 * localStorage. Le service worker fait tourner les jetons quand il rejoue la
 * file sans onglet ouvert ; sans cette reprise au démarrage, l'application
 * repartirait avec un jeton de rafraîchissement déjà révoqué et serait
 * déconnectée dès la première requête.
 */
export async function hydraterJetons() {
  try {
    const local = tokensGet();
    const miroir = await idb.tokensGet();
    if (miroir && (!local || (miroir.exp || 0) > (local.exp || 0))) {
      localStorage.setItem('adlt_tokens', JSON.stringify(miroir));
    }
  } catch { /* miroir indisponible : le localStorage fait foi */ }
}

/**
 * Clé d'idempotence (§ 11.3). `crypto.randomUUID` manque à Safari < 15.4 et à
 * tout contexte non sécurisé : sans repli, une écriture y lèverait une
 * exception au moment de l'envoi.
 */
export function uuid() {
  if (crypto.randomUUID) return crypto.randomUUID();
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0'));
  return `${h.slice(0, 4).join('')}-${h.slice(4, 6).join('')}-${h.slice(6, 8).join('')}-${h.slice(8, 10).join('')}-${h.slice(10).join('')}`;
}

/**
 * Rafraîchissement du couple de jetons. Le serveur révoque le jeton de
 * rafraîchissement présenté à chaque rotation : deux appels concurrents avec le
 * même jeton feraient échouer le second et déconnecteraient la session. La
 * promesse est donc partagée jusqu'à ce que les nouveaux jetons soient écrits —
 * la remise à zéro ne peut pas intervenir avant `tokensSet`.
 */
let refreshing = null;
async function refreshTokens() {
  if (refreshing) return refreshing;
  const t = tokensGet();
  if (!t || !t.refresh) return null;
  refreshing = fetch(REST + 'auth/refresh', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: t.refresh }),
  }).then(async (r) => {
    if (!r.ok) {
      // 401/403 : jeton révoqué ou expiré — la session est réellement finie.
      // 5xx ou panne passagère : on conserve les jetons et on retentera.
      if (r.status === 401 || r.status === 403) { tokensSet(null); bus.emit('logout'); }
      return null;
    }
    const data = await r.json();
    const next = { access: data.access_token, refresh: data.refresh_token, exp: Date.now() + (data.expires_in - 30) * 1000 };
    tokensSet(next);
    return next;
  }).catch(() => null).finally(() => { refreshing = null; });
  return refreshing;
}

async function accessToken() {
  let t = tokensGet();
  if (!t) return null;
  if (Date.now() >= (t.exp || 0)) t = await refreshTokens();
  return t ? t.access : null;
}

export async function login(username, password) {
  const r = await fetch(REST + 'auth/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password, device_label: navigator.userAgent.slice(0, 120) }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) return { ok: false, error: data.message || 'Connexion impossible.' };
  tokensSet({ access: data.access_token, refresh: data.refresh_token, exp: Date.now() + (data.expires_in - 30) * 1000 });
  bus.emit('login', data.me);
  return { ok: true, me: data.me };
}

export async function logout() {
  try { await api('auth/revoke', { method: 'POST' }); } catch { /* hors ligne : on nettoie quand même */ }
  tokensSet(null);
  await idb.clearAll();
  bus.emit('logout');
}

/* ---------------- Requêtes ---------------- */

async function headers(hasBody) {
  const h = { Accept: 'application/json' };
  if (hasBody) h['Content-Type'] = 'application/json';
  if (isStandalone()) {
    const token = await accessToken();
    if (token) h.Authorization = 'Bearer ' + token;
  } else if (CFG.nonce) {
    h['X-WP-Nonce'] = CFG.nonce;
  }
  return h;
}

/**
 * Requête générale.
 * options : method, body, cache (clé de cache lecture hors ligne),
 *           queueable (écriture rejouable hors ligne), idempotencyKey.
 */
export async function api(path, options = {}) {
  const { method = 'GET', body, cache, queueable, idempotencyKey } = options;
  const url = REST + path.replace(/^\//, '');
  const creds = isStandalone() ? 'omit' : 'same-origin';
  try {
    const h = await headers(body !== undefined);
    if (idempotencyKey) h['Idempotency-Key'] = idempotencyKey;
    let res = await fetch(url, {
      method,
      credentials: creds,
      headers: h,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    // Jeton expiré côté serveur : un rafraîchissement puis une relance.
    if (res.status === 401 && isStandalone() && tokensGet()) {
      const t = await refreshTokens();
      if (t) {
        const h2 = await headers(body !== undefined);
        if (idempotencyKey) h2['Idempotency-Key'] = idempotencyKey;
        res = await fetch(url, { method, credentials: creds, headers: h2, body: body !== undefined ? JSON.stringify(body) : undefined });
      }
    }
    const data = await res.json().catch(() => null);
    if (res.ok) {
      if (method === 'GET' && cache) idb.cacheSet(cache, data);
      return { ok: true, data };
    }
    if (res.status === 401) bus.emit('logout');
    return { ok: false, status: res.status, error: (data && data.message) || 'Une erreur est survenue.', code: data && data.code };
  } catch (e) {
    // Réseau indisponible.
    if (method === 'GET' && cache) {
      const cached = await idb.cacheGet(cache);
      if (cached) return { ok: true, data: cached.data, offline: true, cachedAt: cached.t };
    }
    if (queueable) {
      await idb.queueAdd({
        path, method, body,
        idempotencyKey: idempotencyKey || uuid(),
        label: options.label || path,
        t: Date.now(),
      });
      bus.emit('queue-changed');
      requestBackgroundSync();
      return { ok: true, queued: true, offline: true };
    }
    return { ok: false, offline: true, error: 'Action impossible hors ligne.' };
  }
}

/**
 * Récupère une ressource binaire servie par une route authentifiée
 * (photo d'adhésion). En mode PWA l'authentification passe par un en-tête
 * Authorization, que `<img src>` n'envoie pas : l'image doit être récupérée
 * ici puis affichée via un object URL.
 */
export async function apiBlob(url) {
  try {
    const h = await headers(false);
    const res = await fetch(url, {
      method: 'GET',
      credentials: isStandalone() ? 'omit' : 'same-origin',
      headers: h,
    });
    if (!res.ok) return null;
    return await res.blob();
  } catch {
    return null;
  }
}

/* ---------------- File d'attente hors ligne (§ 11.3) ---------------- */

function requestBackgroundSync() {
  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    navigator.serviceWorker.ready.then((reg) => reg.sync.register('adlt-queue')).catch(() => {});
  }
}

export async function queueLength() {
  return (await idb.queueAll()).length;
}

let flushEnCours = null;

/**
 * Rejoue la file. Les refus sont retournés comme liste d'échecs,
 * sans perte silencieuse (§ 11.3).
 */
export async function flushQueue() {
  // La reconnexion et le message du service worker peuvent déclencher deux
  // passes simultanées : sans garde, chaque écriture partirait en double.
  if (flushEnCours) return flushEnCours;
  flushEnCours = (async () => {
    if (!navigator.onLine) return { done: 0, failed: [] };
    const r = await rejouerFile((item) => api(item.path, {
      method: item.method, body: item.body, idempotencyKey: item.idempotencyKey,
    }));
    bus.emit('queue-changed');
    if (r.done || r.failed.length) bus.emit('queue-flushed', r);
    return r;
  })().finally(() => { flushEnCours = null; });
  return flushEnCours;
}

window.addEventListener('online', () => { bus.emit('online', true); flushQueue(); });
window.addEventListener('offline', () => bus.emit('online', false));
navigator.serviceWorker?.addEventListener?.('message', (e) => {
  if (e.data === 'adlt-flush-queue') flushQueue();
});

/* ---------------- Push (§ 11.4) ---------------- */

export async function enablePush(vapidPublic) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return { ok: false, error: 'Push non supporté par ce navigateur.' };
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return { ok: false, error: 'Autorisation refusée.' };
  const reg = await navigator.serviceWorker.ready;
  const raw = atob(vapidPublic.replace(/-/g, '+').replace(/_/g, '/'));
  const key = new Uint8Array([...raw].map((c) => c.charCodeAt(0)));
  const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  return api('push/subscribe', { method: 'POST', body: sub.toJSON() });
}

export async function disablePush() {
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    await api('push/subscribe', { method: 'DELETE', body: { endpoint: sub.endpoint } });
    await sub.unsubscribe();
  }
  return { ok: true };
}
